/**
 * Rename Node Modal Component
 * Simple modal for renaming a node
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { useReactFlow, useViewport, useNodes } from 'reactflow';
import { useWorkflowStore } from '@flow-studio/store'; // import { workflowStore';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { FormLabel, Input, Button } from '../Shared/FormUI';
import { ModalPortal } from './ModalPortal';
import { getCanvasMenuBounds, clampMenuPosition } from '../../utils/canvasMenuBounds';

interface RenameNodeModalProps {
  nodeId: string;
  currentName: string;
  onClose: () => void;
}

const MODAL_WIDTH = 200;
const ANCHOR_GAP = 8;
const VIEWPORT_MARGIN = 8;

export const RenameNodeModal: React.FC<RenameNodeModalProps> = ({
  nodeId,
  currentName,
  onClose,
}) => {
  const { updateProcessElement } = useWorkflowStore();
  const { theme } = useUIStore();
  const [name, setName] = useState(currentName);
  const inputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const reactFlowInstance = useReactFlow();
  const viewport = useViewport(); // re-renders on every pan/zoom frame
  const nodes = useNodes();       // re-renders when a node is dragged/resized
  const node = nodes.find((n) => n.id === nodeId);

  // Screen position of the node's top-center and bottom-center, recomputed on every
  // pan/zoom/drag frame — same technique WorkflowCanvas.tsx uses to keep the right-click
  // context menu anchored to its target (see menuScreenPosition there) instead of going
  // stale after the canvas moves, which a one-time getBoundingClientRect() snapshot would.
  const anchorPoint = useMemo(() => {
    if (!node) return null;
    const width = node.width ?? 0;
    const height = node.height ?? 0;
    const pos = node.positionAbsolute ?? node.position;
    const bottom = reactFlowInstance.flowToScreenPosition({ x: pos.x + width / 2, y: pos.y + height });
    const top = reactFlowInstance.flowToScreenPosition({ x: pos.x + width / 2, y: pos.y });
    return { x: bottom.x, bottomY: bottom.y, topY: top.y };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, viewport, reactFlowInstance]);

  // Measured, viewport-clamped position — null until the first layout pass, so the card
  // renders invisibly (but measurably) rather than flashing at an unclamped position.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const cardEl = cardRef.current;
    if (!anchorPoint || !cardEl) { setPos(null); return; }

    const cardRect = cardEl.getBoundingClientRect();
    const bounds = getCanvasMenuBounds(cardEl);

    // Preferred: directly below the node.
    let top = anchorPoint.bottomY + ANCHOR_GAP;

    // Not enough room below — flip above the node instead, but only if that actually fits;
    // otherwise keep it below and let the final clamp keep it on-screen (matches the
    // node's bottom edge being pinned near the very top of the canvas, a rare corner case).
    if (top + cardRect.height + VIEWPORT_MARGIN > bounds.bottom) {
      const flippedTop = anchorPoint.topY - cardRect.height - ANCHOR_GAP;
      if (flippedTop >= bounds.top) top = flippedTop;
    }

    const left = Math.min(
      Math.max(anchorPoint.x - cardRect.width / 2, VIEWPORT_MARGIN),
      window.innerWidth - cardRect.width - VIEWPORT_MARGIN
    );

    setPos(clampMenuPosition({ top, left }, cardRect, bounds, VIEWPORT_MARGIN));
  }, [anchorPoint]);

  useEffect(() => {
    // Auto-focus and select all text
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, []);

  // Anchored mode has no full-screen backdrop (see overlayStyle below), so outside-clicks
  // are detected the same way NodeContextMenu does: a document-level listener, plus the
  // flow-studio:pane-click event for clicks that land on the React Flow canvas — React
  // Flow's pan handler calls stopImmediatePropagation() on pane mousedown, which a plain
  // document listener never sees.
  useEffect(() => {
    if (!anchorPoint) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('flow-studio:pane-click', onClose);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('flow-studio:pane-click', onClose);
    };
  }, [anchorPoint, onClose]);

  const handleSave = () => {
    if (name.trim()) {
      updateProcessElement(nodeId, { displayName: name.trim() });
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // With an anchorPoint, there is no full-screen backdrop at all — a fixed, full-viewport
  // div (even fully transparent) would sit on top of the canvas and swallow every
  // wheel/scroll/drag event meant for React Flow, breaking panning and zooming while the
  // popup is open. Outside-click detection is handled by the effect above instead. Without
  // an anchorPoint (rare fallback — node not found), keep the old centered dialog with its
  // backdrop.
  const overlayStyle: React.CSSProperties | undefined = anchorPoint
    ? undefined
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
      };

  // Colors match ModuleCard.tsx (the EdgeInteract Explorer cards): dark navy card,
  // subtle green-tinted border, soft shadow — the app's standard card look.
  const anchoredPosition: React.CSSProperties = anchorPoint
    ? {
        position: 'fixed',
        top: pos ? `${pos.top}px` : '0px',
        left: pos ? `${pos.left}px` : '0px',
        // Hidden until the first measurement resolves a clamped position — avoids a
        // visible flash/jump at the unclamped (potentially off-screen) spot.
        visibility: pos ? 'visible' : 'hidden',
      }
    : {};

  const modalStyle: React.CSSProperties = {
    ...anchoredPosition,
    zIndex: 10000,
    background: theme === 'dark' ? '#0f172a' : '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: `${MODAL_WIDTH}px`,
    boxShadow: theme === 'dark' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${theme === 'dark' ? 'rgba(58, 140, 69, 0.3)' : '#e5e7eb'}`,
  };

  const headerStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#e5e7eb'}`,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '13px',
    fontWeight: 700,
    color: theme === 'dark' ? '#f8fafc' : '#111827',
  };

  const contentStyle: React.CSSProperties = {
    padding: '12px 14px',
  };

  const footerStyle: React.CSSProperties = {
    padding: '10px 14px',
    borderTop: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#e5e7eb'}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '6px',
  };

  // Override the shared .form-button's larger padding/font-size (10px 24px / 14px) so
  // both buttons fit inside this compact modal without wrapping or overflowing.
  const compactButtonStyle: React.CSSProperties = {
    padding: '6px 12px',
    fontSize: '12px',
  };

  const card = (
    <div ref={cardRef} style={modalStyle} onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleStyle}>Rename Node</div>
      </div>

      {/* Content */}
      <div style={contentStyle}>
        <FormLabel>Node Name</FormLabel>
        <Input
          ref={inputRef}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter new name"
        />
        <div style={{ marginTop: '8px', fontSize: '12px', color: theme === 'dark' ? '#9ca3af' : '#6b7280' }}>
          Press Enter to save, Escape to cancel
        </div>
      </div>

      {/* Footer */}
      <div style={footerStyle}>
        <Button variant="secondary" onClick={onClose} style={compactButtonStyle}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={!name.trim()} style={compactButtonStyle}>Rename</Button>
      </div>
    </div>
  );

  return (
    <ModalPortal>
      {overlayStyle ? (
        <div style={overlayStyle} onClick={onClose}>
          {card}
        </div>
      ) : (
        card
      )}
    </ModalPortal>
  );
};
